import { cn } from '@/lib/cn'
import { useTicker } from '@/hooks/use-ticker'
import type { Giveaway } from '@/models/giveaway'
import { SkeletonLine } from '../ui/Skeleton'

/** Under an hour left, the countdown box warms to gold. */
const URGENT_MS = 60 * 60_000

interface EntriesStripProps {
  giveaway: Giveaway
  className?: string
}

/**
 * The "Entries / Your Entries / Time Left" strip, shared by the giveaway card,
 * the details overlay and the new-giveaway preview — three hand-copied blocks
 * in the original.
 */
export function EntriesStrip({ giveaway, className }: EntriesStripProps) {
  // Re-render each second so the countdown advances.
  useTicker(1000, !giveaway.has_ended)

  const remaining = giveaway.display_time_until_finish
  const msLeft = giveaway.finishes_at.getTime() - Date.now()
  const isUrgent = !giveaway.has_ended && msLeft <= URGENT_MS

  return (
    <div className={cn('giveaway-entries', className)}>
      {giveaway.shows_entry_count ? (
        <div
          className="giveaway-entries-box"
          title={giveaway.hide_entries ? 'Hidden from everyone but you' : undefined}
        >
          <div className="giveaway-entries-name">Entries</div>
          <div className="giveaway-entries-digit">
            {giveaway.display_total_entries.toLocaleString()}
          </div>
        </div>
      ) : null}
      <div className="giveaway-entries-box">
        <div className="giveaway-entries-name">Your Entries</div>
        <div className="giveaway-entries-digit">{giveaway.display_my_entries.toLocaleString()}</div>
      </div>
      <div className={cn('giveaway-entries-box', isUrgent && 'is-urgent')}>
        <div className="giveaway-entries-name">{giveaway.has_ended ? 'Ended' : 'Time Left'}</div>
        <div className="giveaway-entries-digit">
          {remaining.digit}
          <span className="giveaway-entries-days">{remaining.text}</span>
        </div>
      </div>
    </div>
  )
}

/** The strip's shape while loading: the real labels, placeholder numbers. */
export function EntriesStripSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('giveaway-entries', className)} aria-hidden="true">
      {['Entries', 'Your Entries', 'Time Left'].map((label) => (
        <div className="giveaway-entries-box" key={label}>
          <div className="giveaway-entries-name">{label}</div>
          <div className="giveaway-entries-digit">
            <SkeletonLine width="2.4em" />
          </div>
        </div>
      ))}
    </div>
  )
}
